import moment from "moment";
import {Booking, Tour, Coordinator} from "./interfaces";
import {Guide} from "./interfaces";
import {LoginButton, GuideList, TourListContainer, TourItem, TourHeader, TourId, BookingList, BookingItem, BookingTitle, BookingDetails, BookingDetail, TourGuideList, GuideItem} from "../../styles/Styles";
import React, {useState} from "react";
import AppModal from "./AppModal";
import {FaCheck, FaTimes} from "react-icons/fa";
import styled from "styled-components";
import {Languages} from "./languages";

/** TourOutput has no tourLabel; use title / tourTitle from the API. */
const labelFromTour = (tour: Tour): string =>
    [tour.tourTitle, tour.title].find((s) => s && String(s).trim()) || "";

const GuideInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const ConfirmationStatus = styled.div<{ $confirmed: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: ${props => props.$confirmed ? '#4CAF50' : '#f44336'};
    font-size: 0.9rem;
`;

const NoGuideLabel = styled.div`
    background-color: #f44336;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 4px;
    font-size: 0.9rem;
    font-weight: 500;
    margin-left: 1rem;
`;

const StyledTourItem = styled(TourItem)<{ $hasGuide: boolean }>`
    border: 2px solid ${props => props.$hasGuide ? '#ddd' : '#f44336'};
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
`;

const NoCoordinatorLabel = styled(NoGuideLabel)``;

const CoordinatorSection = styled.div`
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
`;

const LanguageSection = styled(CoordinatorSection)``;

const LanguageSelect = styled.select`
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    margin-bottom: 1rem;
`;

interface TourListProps {
    tours: Tour[];
    guides?: Guide[];
    coordinators?: Coordinator[];
    updateTourGuides: (tourId: string, selectedGuides: string[]) => void;
    updateTourCoordinator?: (tourId: string, coordinatorId: string) => void;
    updateTourLanguage?: (tourId: string, language: Languages) => void;
    onDeleteTour?: (tourId: string) => void;
}

const DeleteTourButton = styled.button`
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    margin-top: 1rem;
    font-size: 0.95rem;

    &:hover {
        background-color: #c82333;
    }
`;

const TourList = ({tours, guides, coordinators, updateTourGuides, updateTourCoordinator, updateTourLanguage, onDeleteTour}: TourListProps) => {
    const [openModal, setOpenModal] = useState(false);
    const [openCoordinatorModal, setOpenCoordinatorModal] = useState(false);
    const [openLanguageModal, setOpenLanguageModal] = useState(false);
    const [activeTour, setActiveTour] = useState("");
    const [selectedGuides, setSelectedGuides] = useState<string[]>([]);
    const [selectedCoordinatorId, setSelectedCoordinatorId] = useState("");
    const [selectedLanguage, setSelectedLanguage] = useState<Languages | "">("");

    const LanguagesArray = Object.values(Languages);

    const selectHandler = (id: string) => {
        setSelectedGuides(prev => {
            const isSelected = prev.includes(id);
            if (isSelected) {
                return prev.filter(guideId => guideId !== id);
            } else {
                return [...prev, id];
            }
        });
    }

    const isGuideSelected = (guideId: string) => {
        return selectedGuides.includes(guideId);
    }

    const updateGuideHandler = () => {
        updateTourGuides(activeTour, selectedGuides);
        setActiveTour("");
        setOpenModal(false);
    }

    const updateGuideModalHandler = (tourId: string) => {
        setActiveTour(tourId);
        // Clear previous selections
        setSelectedGuides([]);
        // If tour has a guide, add it to selected guides
        const currentTour = tours.find(tour => tour._id === tourId);
        if (currentTour?.guide) {
            setSelectedGuides(currentTour.guide.map(guide => guide._id));
        }
        setOpenModal(true);
    }

    const updateCoordinatorModalHandler = (tourId: string) => {
        setActiveTour(tourId);
        const currentTour = tours.find(tour => tour._id === tourId);
        setSelectedCoordinatorId(currentTour?.coordinator?.[0]?._id ?? "");
        setOpenCoordinatorModal(true);
    }

    const updateCoordinatorHandler = () => {
        if (updateTourCoordinator && selectedCoordinatorId) {
            updateTourCoordinator(activeTour, selectedCoordinatorId);
        }
        setActiveTour("");
        setSelectedCoordinatorId("");
        setOpenCoordinatorModal(false);
    }

    const updateLanguageModalHandler = (tourId: string) => {
        setActiveTour(tourId);
        const currentTour = tours.find(tour => tour._id === tourId);
        setSelectedLanguage(currentTour?.language ?? "");
        setOpenLanguageModal(true);
    }

    const updateLanguageHandler = () => {
        if (updateTourLanguage && selectedLanguage) {
            updateTourLanguage(activeTour, selectedLanguage);
        }
        setActiveTour("");
        setSelectedLanguage("");
        setOpenLanguageModal(false);
    }

    return (<TourListContainer>
        {tours?.length === 0
            ? 'No tours yet'
            : tours?.map((tour: Tour, index: number) => {
                const totals = {
                    adults: 0,
                    youths: 0,
                    children: 0,
                    enfants: 0
                };

                tour?.bookings?.forEach(booking => {
                    if (booking.client) {
                        totals.adults += booking.client.clientAdult || 0;
                        totals.youths += booking.client.clientYouth || 0;
                        totals.children += booking.client.clientChild || 0;
                        totals.enfants += booking.client.clientEnfant || 0;
                    }
                });

                const hasGuide = Boolean(tour.guide && tour.guide.length > 0);
                const coordinatorName = tour.coordinator?.[0]
                    ? `${tour.coordinator[0].profile.firstName} ${tour.coordinator[0].profile.lastName}`
                    : null;

                return (
                    <StyledTourItem key={index} $hasGuide={hasGuide}>
                        <TourHeader>
                            <div>
                                <TourId>
                                    Tour: {labelFromTour(tour) || "—"}
                                    {!hasGuide && <NoGuideLabel>No guide set</NoGuideLabel>}
                                </TourId>
                                <BookingDetails>
                                    <BookingDetail>Total Adults: {totals.adults}</BookingDetail>
                                    <BookingDetail>Total Youths: {totals.youths}</BookingDetail>
                                    <BookingDetail>Total Children: {totals.children}</BookingDetail>
                                    <BookingDetail>Total Enfants: {totals.enfants}</BookingDetail>
                                    <BookingDetail>Language: {tour.language ?? 'Not set'}</BookingDetail>
                                </BookingDetails>
                            </div>
                            <LoginButton onClick={() => updateGuideModalHandler(tour?._id)}>Select guide(-s)</LoginButton>
                        </TourHeader>
                        <BookingList>
                            {tour?.bookings?.map((booking: Booking) => (
                                <BookingItem key={booking._id}>
                                    <BookingTitle>{booking?.tourItem?.title}</BookingTitle>
                                    <BookingDetails>
                                        <BookingTitle>{booking.bookingCode}</BookingTitle>
                                        <BookingDetail>Date: {moment(booking?.bookingDate).format("D MMM YYYY h:mm a")}</BookingDetail>
                                        <BookingDetail>Status: {booking.status}</BookingDetail>
                                        <BookingDetail>Price: €{booking.priceNet}</BookingDetail>
                                        <BookingDetail>Adults: {booking.client?.clientAdult || 0}</BookingDetail>
                                        <BookingDetail>Youths: {booking.client?.clientYouth || 0}</BookingDetail>
                                        <BookingDetail>Children: {booking.client?.clientChild || 0}</BookingDetail>
                                        <BookingDetail>Enfants: {booking.client?.clientEnfant || 0}</BookingDetail>
                                    </BookingDetails>
                                </BookingItem>
                            ))}
                        </BookingList>
                        {tour?.guide && (
                            <TourGuideList>
                                {tour.guide.map((guide) => (
                                    <GuideItem key={guide._id}>
                                        <GuideInfo>
                                            <span>{guide.profile.firstName} {guide.profile.lastName}</span>
                                            <ConfirmationStatus $confirmed={guide.isConfirmedByGuide === true}>
                                                {guide.isConfirmedByGuide === true ? <FaCheck /> : <FaTimes />}
                                                {guide.isConfirmedDate ? `Confirmed on ${moment(guide.isConfirmedDate).format('MMM D, YYYY')}` : 'Not confirmed'}
                                            </ConfirmationStatus>
                                        </GuideInfo>
                                    </GuideItem>
                                ))}
                            </TourGuideList>
                        )}
                        {updateTourCoordinator && (
                            <CoordinatorSection>
                                <div>
                                    <strong>Coordinator:</strong>{' '}
                                    {coordinatorName ?? (
                                        <NoCoordinatorLabel as="span">No coordinator set</NoCoordinatorLabel>
                                    )}
                                </div>
                                <LoginButton type="button" onClick={() => updateCoordinatorModalHandler(tour._id)}>
                                    Select coordinator
                                </LoginButton>
                            </CoordinatorSection>
                        )}
                        {updateTourLanguage && (
                            <LanguageSection>
                                <div>
                                    <strong>Language:</strong>{' '}
                                    {tour.language ?? (
                                        <NoCoordinatorLabel as="span">Not set</NoCoordinatorLabel>
                                    )}
                                </div>
                                <LoginButton type="button" onClick={() => updateLanguageModalHandler(tour._id)}>
                                    Select language
                                </LoginButton>
                            </LanguageSection>
                        )}
                        {onDeleteTour && (
                            <DeleteTourButton type="button" onClick={() => onDeleteTour(tour._id)}>
                                Delete Tour
                            </DeleteTourButton>
                        )}
                    </StyledTourItem>
                );
            })}
        <AppModal isOpen={openModal} onClose={()=>setOpenModal(false)} className={""}>
            <GuideList>
                {guides?.map((guide: Guide, index: number) => (
                    <GuideItem
                        key={index}
                        className={`guide-item ${isGuideSelected(guide._id) ? 'selected' : ''}`}
                        onClick={() => selectHandler(guide._id)}
                    >
                        {isGuideSelected(guide._id) && <FaCheck />}
                        {guide.profile.firstName} {guide?.profile?.lastName}
                    </GuideItem>
                ))}
                <button disabled={selectedGuides.length === 0} onClick={() => updateGuideHandler()}>
                    Update guide{selectedGuides?.length > 0 && "(-s)"}
                </button>
            </GuideList>
        </AppModal>
        <AppModal isOpen={openCoordinatorModal} onClose={() => setOpenCoordinatorModal(false)} className={""}>
            <GuideList>
                {coordinators?.map((coordinator: Coordinator) => (
                    <GuideItem
                        key={coordinator._id}
                        className={`guide-item ${selectedCoordinatorId === coordinator._id ? 'selected' : ''}`}
                        onClick={() => setSelectedCoordinatorId(coordinator._id)}
                    >
                        {selectedCoordinatorId === coordinator._id && <FaCheck />}
                        {coordinator.profile.firstName} {coordinator.profile.lastName}
                    </GuideItem>
                ))}
                <button disabled={!selectedCoordinatorId} onClick={() => updateCoordinatorHandler()}>
                    Update coordinator
                </button>
            </GuideList>
        </AppModal>
        <AppModal isOpen={openLanguageModal} onClose={() => setOpenLanguageModal(false)} className={""}>
            <GuideList>
                <LanguageSelect
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value as Languages)}
                >
                    <option value="">Select a language</option>
                    {LanguagesArray.map((lang) => (
                        <option key={lang} value={lang}>
                            {lang}
                        </option>
                    ))}
                </LanguageSelect>
                <button disabled={!selectedLanguage} onClick={() => updateLanguageHandler()}>
                    Update language
                </button>
            </GuideList>
        </AppModal>
    </TourListContainer>)
}

export default TourList;
